<template>
    <Head :title="`Ticket ${ticket.ticket_number}`" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="mb-6">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ ticket.ticket_number }}</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">{{ ticket.subject }}</p>
                    </div>
                    <InfoButton title="Ticket Details & Management" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The Ticket Details page is your command center for managing a helpdesk ticket through its
                            entire lifecycle. From initial assignment to resolution, this page provides all tools needed
                            to track progress, manage resources, document work performed, and collaborate with team members.
                            The page dynamically adapts based on the ticket's current status, showing relevant actions and
                            forms at each stage.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Workflow Actions:</strong> Available actions change based on status - Open/Assigned
                              tickets can be scheduled, Scheduled tickets can be started, In Progress tickets can be
                              completed directly<br>
                            • <strong>Assignment:</strong> Assign or reassign tickets to technicians at any stage
                              (except resolved/cancelled)<br>
                            • <strong>Tasks Tab:</strong> Add, edit, complete, and track checklist items for the ticket.
                              Add completion notes when marking tasks done<br>
                            • <strong>Labor Tab:</strong> Record labor hours and costs once work begins. Edit or delete
                              entries during in progress/on hold status<br>
                            • <strong>Materials Tab:</strong> Track materials used, quantities, and costs. Builds
                              accurate cost records<br>
                            • <strong>External Repairs Tab:</strong> Document work performed by external service providers,
                              including invoices<br>
                            • <strong>Comments Tab:</strong> Collaborate with team members through threaded comments<br>
                            • <strong>Attachments Tab:</strong> Upload photos, documents, or PDFs at any stage to
                              document work<br>
                            • <strong>Logs Tab:</strong> Review complete audit trail of all changes, assignments,
                              and status updates<br>
                            • <strong>SLA Policy Tab:</strong> Monitor SLA compliance and deadlines
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Status badges</strong> provide visual cues - color-coded for quick identification<br>
                            • <strong>On Hold feature</strong> pauses work without losing data - use when waiting for
                              parts or approvals<br>
                            • <strong>Cost tracking</strong> is automatic - system calculates total from labor,
                              materials, and external repairs<br>
                            • <strong>Task completion notes</strong> capture important details about how work was
                              performed<br>
                            • <strong>Direct resolution</strong> - tickets can be resolved directly from in progress
                              status without review step<br>
                            • <strong>Reassignment</strong> preserves all history - use when technicians change or
                              need help<br>
                            • <strong>Activity log</strong> is automatically maintained - perfect for audits and
                              troubleshooting<br>
                            • <strong>Upload photos</strong> before and after work to document condition and completed
                              repairs
                        </p>
                    </InfoButton>
                </div>
                <div class="flex items-center space-x-3">
                    <button
                        @click="showPrintPdfDialog = true"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md font-semibold text-xs uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Print PDF
                    </button>
                    <Link
                        :href="route('tickets.index')"
                        class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </Link>
                </div>
            </div>
        </div>

        <!-- Status Badge and Info -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                    <span
                        class="px-3 py-1 text-sm font-semibold rounded-full"
                        :class="getStatusClass(ticket.status)"
                    >
                        {{ ticket.status_label }}
                    </span>
                    <span class="text-sm text-gray-600 dark:text-gray-400">
                        Priority: <span :class="getPriorityClass(ticket.priority)" class="font-semibold">{{ ticket.priority_label }}</span>
                    </span>
                    <span v-if="ticket.category" class="text-sm text-gray-600 dark:text-gray-400">
                        Category: <span class="font-semibold">{{ ticket.category.name }}</span>
                    </span>
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">
                    Created: {{ formatDate(ticket.created_at) }}
                </div>
            </div>
        </div>

        <!-- Ticket Information Card -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ticket Information</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Reporter</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ reporter?.name || 'N/A' }}</p>
                </div>
                <div v-if="assignedTo">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Assigned To</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ assignedTo.name }}</p>
                </div>
                <div v-if="subscriptionFeatures.equipment_module && ticket.it_equipment">
                    <p class="text-sm text-gray-600 dark:text-gray-400">IT Equipment</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ ticket.it_equipment.name }}</p>
                </div>
                <div v-if="ticket.location">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Location</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ ticket.location.path_with_type }}</p>
                </div>
                <div v-if="ticket.category">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Category</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ ticket.category.name }}</p>
                </div>
                <div v-if="ticket.subcategory">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Subcategory</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ ticket.subcategory.name }}</p>
                </div>
            </div>
            <div class="mt-4">
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">Description</p>
                <p class="text-gray-900 dark:text-white">{{ ticket.description }}</p>
            </div>
        </div>

        <!-- Assignment & Schedule Section (scheduled/in_progress/on_hold/resolved) -->
        <div v-if="['scheduled', 'in_progress', 'on_hold', 'resolved'].includes(ticket.status) && (canAssign || (ticket.assigned_to && (isReporter || isAssigned)) || ticket.scheduled_start || ticket.scheduled_end)" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Assignment & Scheduling</h2>

            <div class="space-y-4">
                <!-- Assign/Reassign Option - Show entire section if canAssign OR if user is reporter/assigned -->
                <div v-if="canAssign || (ticket.assigned_to && (isReporter || isAssigned))" class="border dark:border-gray-700 rounded-lg p-4">
                    <!-- Show Current Assignee if ticket is assigned -->
                    <div v-if="ticket.assigned_to && assignedTo" :class="(ticket.status === 'resolved' || !canAssign) ? '' : 'mb-4 pb-4 border-b dark:border-gray-700'">
                        <h3 class="font-medium text-gray-900 dark:text-white mb-2">{{ ticket.status === 'resolved' ? 'Assigned To' : 'Currently Assigned To' }}</h3>
                        <div class="flex items-center space-x-2">
                            <div class="flex-shrink-0">
                                <div class="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                    <span class="text-blue-600 dark:text-blue-400 font-medium text-sm">
                                        {{ assignedTo.name ? assignedTo.name.charAt(0).toUpperCase() : 'U' }}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p class="text-sm font-medium text-gray-900 dark:text-white">
                                    {{ assignedTo.name || 'Unknown User' }}
                                </p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ assignedTo.email }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Reassign Form (Only show if not resolved AND user has assign permission) -->
                    <div v-if="ticket.status !== 'resolved' && canAssign">
                        <h3 class="font-medium text-gray-900 dark:text-white mb-3">
                            {{ ticket.assigned_to ? 'Reassign to Different Technician' : 'Assign Ticket' }}
                        </h3>
                        <div class="flex items-end space-x-3">
                            <div class="flex-1">
                                <select
                                    v-model="assignForm.assigned_to"
                                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option value="">Select Technician</option>
                                    <option v-for="tech in technicians" :key="tech.id" :value="tech.id">
                                        {{ tech.name }}
                                    </option>
                                </select>
                            </div>
                            <button
                                @click="assignTicket"
                                :disabled="!assignForm.assigned_to || assignForm.processing"
                                class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {{ ticket.assigned_to ? 'Reassign' : 'Assign' }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Schedule Info (Read-only if exists) -->
                <div v-if="ticket.scheduled_start || ticket.scheduled_end" class="border dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                    <h3 class="font-medium text-gray-900 dark:text-white mb-3">Scheduled Times</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div v-if="ticket.scheduled_start">
                            <p class="text-sm text-gray-600 dark:text-gray-400">Scheduled Start</p>
                            <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(ticket.scheduled_start) }}</p>
                        </div>
                        <div v-if="ticket.scheduled_end">
                            <p class="text-sm text-gray-600 dark:text-gray-400">Scheduled End</p>
                            <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(ticket.scheduled_end) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Take Action Card (Open/Assigned Status) -->
        <div v-if="['open', 'assigned'].includes(ticket.status) && (canAssign || canEdit)" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Assignment & Scheduling</h2>
            <div class="space-y-4">
                <!-- Assign/Reassign Option -->
                <div v-if="canAssign" class="border dark:border-gray-700 rounded-lg p-4">
                    <!-- Show Current Assignee if ticket is assigned -->
                    <div v-if="ticket.assigned_to && assignedTo" class="mb-4 pb-4 border-b dark:border-gray-700">
                        <h3 class="font-medium text-gray-900 dark:text-white mb-2">Currently Assigned To</h3>
                        <div class="flex items-center space-x-2">
                            <div class="flex-shrink-0">
                                <div class="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                    <span class="text-blue-600 dark:text-blue-400 font-medium text-sm">
                                        {{ assignedTo.name ? assignedTo.name.charAt(0).toUpperCase() : 'U' }}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p class="text-sm font-medium text-gray-900 dark:text-white">
                                    {{ assignedTo.name || 'Unknown User' }}
                                </p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ assignedTo.email }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Assign/Reassign Form -->
                    <h3 class="font-medium text-gray-900 dark:text-white mb-3">
                        {{ ticket.assigned_to ? 'Reassign to Different Technician' : 'Assign Ticket' }}
                    </h3>
                    <div class="flex items-end space-x-3">
                        <div class="flex-1">
                            <select
                                v-model="assignForm.assigned_to"
                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value="">Select Technician</option>
                                <option v-for="tech in technicians" :key="tech.id" :value="tech.id">
                                    {{ tech.name }}
                                </option>
                            </select>
                        </div>
                        <button
                            @click="assignTicket"
                            :disabled="!assignForm.assigned_to || assignForm.processing"
                            class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {{ ticket.assigned_to ? 'Reassign' : 'Assign' }}
                        </button>
                    </div>
                </div>

                <!-- Schedule Option -->
                <div v-if="canEdit" class="border dark:border-gray-700 rounded-lg p-4">
                    <h3 class="font-medium text-gray-900 dark:text-white mb-3">Schedule Work</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scheduled Start</label>
                            <input
                                v-model="scheduleForm.scheduled_start"
                                type="datetime-local"
                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scheduled End</label>
                            <input
                                v-model="scheduleForm.scheduled_end"
                                type="datetime-local"
                                class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div class="mt-3">
                        <button
                            @click="scheduleTicket"
                            :disabled="!scheduleForm.scheduled_start || !scheduleForm.scheduled_end || scheduleForm.processing"
                            class="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Schedule
                        </button>
                    </div>
                </div>

                <!-- Other Actions -->
                <div class="border dark:border-gray-700 rounded-lg p-4">
                    <h3 class="font-medium text-gray-900 dark:text-white mb-3">Other Actions</h3>
                    <div class="flex flex-wrap gap-3">
                        <button
                            @click="showStartWorkDialog = true"
                            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            Start Work Immediately
                        </button>
                        <button
                            v-if="canCancel"
                            @click="showCancelDialog = true"
                            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                            Cancel Ticket
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Scheduled Status Actions -->
        <div v-if="ticket.status === 'scheduled'" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions</h2>
            <div class="flex space-x-3">
                <button
                    @click="startTicket"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                    Start Work
                </button>
            </div>
        </div>

        <!-- In Progress Status Actions -->
        <div v-if="ticket.status === 'in_progress' && (isAssigned || canEdit)" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions</h2>
            <div class="flex space-x-3">
                <button
                    @click="showPutOnHoldDialog = true"
                    class="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                    Put on Hold
                </button>
                <button
                    v-if="canComplete"
                    @click="showCompleteDialog = true"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                    Resolve Ticket
                </button>
            </div>
        </div>

        <!-- On Hold Status Actions -->
        <div v-if="ticket.status === 'on_hold'" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions</h2>
            <div class="flex space-x-3">
                <button
                    @click="resumeTicket"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                    Resume
                </button>
                <button
                    v-if="canComplete"
                    @click="showCompleteDialog = true"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                    Resolve Ticket
                </button>
            </div>
        </div>

        <!-- Resolved Status Actions -->
        <div v-if="ticket.status === 'resolved' && canReopen" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions</h2>
            <button
                @click="reopenTicket"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
                Reopen Ticket
            </button>
        </div>

        <!-- Tabs -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <!-- Tab Headers -->
            <div class="border-b border-gray-200 dark:border-gray-700">
                <nav class="flex -mb-px overflow-x-auto">
                    <button
                        v-for="tab in tabs"
                        :key="tab.key"
                        @click="activeTab = tab.key"
                        :class="[
                            'px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap',
                            activeTab === tab.key
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        ]"
                    >
                        {{ tab.label }}
                        <span v-if="tab.count !== undefined" class="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            {{ tab.count }}
                        </span>
                    </button>
                </nav>
            </div>

            <!-- Tab Content -->
            <div class="p-6">
                <!-- Tasks Tab -->
                <div v-show="activeTab === 'tasks'">
                    <div v-if="canModifyTasksOrCosts && ['scheduled', 'in_progress', 'on_hold'].includes(ticket.status)" class="mb-4">
                        <div class="flex space-x-3">
                            <input
                                v-model="newTask"
                                type="text"
                                placeholder="Add a new task..."
                                class="flex-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                @keyup.enter="addTask"
                            />
                            <button
                                @click="addTask"
                                :disabled="!newTask.trim()"
                                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                Add Task
                            </button>
                        </div>
                    </div>
                    <div v-if="ticket.tasks && ticket.tasks.length > 0" class="space-y-2">
                        <div
                            v-for="task in ticket.tasks"
                            :key="task.id"
                            class="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                            <input
                                v-if="canModifyTasksOrCosts && ['scheduled', 'in_progress', 'on_hold'].includes(ticket.status)"
                                type="checkbox"
                                :checked="task.is_completed"
                                @change="handleTaskToggle(task)"
                                class="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div v-else class="h-4 w-4 mt-1 flex items-center justify-center">
                                <svg v-if="task.is_completed" class="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                </svg>
                                <span v-else class="h-4 w-4 border-2 border-gray-300 dark:border-gray-600 rounded"></span>
                            </div>
                            <div class="ml-3 flex-1">
                                <span :class="{ 'line-through text-gray-500 dark:text-gray-400': task.is_completed }" class="text-gray-900 dark:text-white">
                                    {{ task.task }}
                                </span>
                                <div v-if="task.is_completed && task.notes" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Notes: {{ task.notes }}
                                </div>
                            </div>
                            <div v-if="canModifyTasksOrCosts && !task.is_completed && ['scheduled', 'in_progress', 'on_hold'].includes(ticket.status)" class="flex space-x-2 ml-3">
                                <button
                                    @click="openTaskEditModal(task)"
                                    class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                    title="Edit task"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                                <button
                                    @click="deleteTask(task.id)"
                                    class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                    title="Delete task"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                                <button
                                    @click="openTaskCompletionModal(task)"
                                    class="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                                    title="Complete task"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 dark:text-gray-400 text-center py-8">No tasks added yet</p>
                </div>

                <!-- Labor Tab -->
                <div v-show="activeTab === 'labor'">
                    <div v-if="['in_progress', 'on_hold', 'resolved', 'cancelled', 'closed'].includes(ticket.status)">
                        <div v-if="canModifyTasksOrCosts && ['in_progress', 'on_hold'].includes(ticket.status)" class="mb-4">
                            <button
                                @click="openLaborModal()"
                                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Add Labor Entry
                            </button>
                        </div>
                        <div v-if="ticket.labor && ticket.labor.length > 0" class="space-y-3">
                            <div
                                v-for="labor in ticket.labor"
                                :key="labor.id"
                                class="border dark:border-gray-700 rounded-lg p-4"
                            >
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <p class="font-medium text-gray-900 dark:text-white">{{ labor.description }}</p>
                                        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">Hours: {{ labor.hours }} | Cost: {{ formatMoney(labor.total_cost) }}</p>
                                    </div>
                                    <div v-if="canEditCosts" class="flex space-x-2">
                                        <button
                                            @click="openLaborModal(labor)"
                                            class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        <button
                                            @click="deleteLabor(labor.id)"
                                            class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-gray-500 dark:text-gray-400 text-center py-8">No labor entries added yet</p>
                    </div>
                    <div v-else>
                        <p class="text-gray-500 dark:text-gray-400 text-center py-8">Labor tracking is available once work starts</p>
                    </div>
                </div>

                <!-- Materials Tab -->
                <div v-show="activeTab === 'materials'">
                    <div v-if="['in_progress', 'on_hold', 'resolved', 'cancelled', 'closed'].includes(ticket.status)">
                        <div v-if="canModifyTasksOrCosts && ['in_progress', 'on_hold'].includes(ticket.status)" class="mb-4">
                            <button
                                @click="openMaterialModal()"
                                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Add Material
                            </button>
                        </div>
                        <div v-if="ticket.materials && ticket.materials.length > 0" class="space-y-3">
                            <div
                                v-for="material in ticket.materials"
                                :key="material.id"
                                class="border dark:border-gray-700 rounded-lg p-4"
                            >
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <p class="font-medium text-gray-900 dark:text-white">{{ material.material_name }}</p>
                                        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ material.description }}</p>
                                        <p class="text-sm text-gray-600 dark:text-gray-400">Quantity: {{ material.quantity }} | Cost: {{ formatMoney(material.total_cost) }}</p>
                                    </div>
                                    <div v-if="canEditCosts" class="flex space-x-2">
                                        <button
                                            @click="openMaterialModal(material)"
                                            class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        <button
                                            @click="deleteMaterial(material.id)"
                                            class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-gray-500 dark:text-gray-400 text-center py-8">No materials added yet</p>
                    </div>
                    <div v-else>
                        <p class="text-gray-500 dark:text-gray-400 text-center py-8">Materials tracking is available once work starts</p>
                    </div>
                </div>

                <!-- External Repairs Tab -->
                <div v-show="activeTab === 'external'">
                    <div v-if="['in_progress', 'on_hold', 'resolved', 'cancelled', 'closed'].includes(ticket.status)">
                        <div v-if="canModifyTasksOrCosts && ['in_progress', 'on_hold'].includes(ticket.status)" class="mb-4">
                            <button
                                @click="openExternalRepairModal()"
                                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Add External Repair
                            </button>
                        </div>
                        <div v-if="ticket.external_repairs && ticket.external_repairs.length > 0" class="space-y-3">
                            <div
                                v-for="repair in ticket.external_repairs"
                                :key="repair.id"
                                class="border dark:border-gray-700 rounded-lg p-4"
                            >
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <p class="font-medium text-gray-900 dark:text-white">{{ repair.vendor_name }}</p>
                                        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ repair.description }}</p>
                                        <p class="text-sm text-gray-600 dark:text-gray-400">Cost: {{ formatMoney(repair.cost) }}</p>
                                        <p v-if="repair.invoice_number" class="text-xs text-gray-500 dark:text-gray-400">Invoice: {{ repair.invoice_number }}</p>
                                    </div>
                                    <div v-if="canEditCosts" class="flex space-x-2">
                                        <button
                                            @click="openExternalRepairModal(repair)"
                                            class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        <button
                                            @click="deleteExternalRepair(repair.id)"
                                            class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                        >
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-gray-500 dark:text-gray-400 text-center py-8">No external repairs added yet</p>
                    </div>
                    <div v-else>
                        <p class="text-gray-500 dark:text-gray-400 text-center py-8">External repairs tracking is available once work starts</p>
                    </div>
                </div>

                <!-- Comments Tab -->
                <div v-show="activeTab === 'comments'">
                    <div v-if="!['resolved', 'cancelled', 'closed'].includes(ticket.status)" class="mb-6">
                        <textarea
                            v-model="newComment"
                            rows="3"
                            placeholder="Add a comment..."
                            class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        ></textarea>
                        <div class="mt-2 flex justify-end">
                            <button
                                @click="addComment"
                                :disabled="!newComment.trim()"
                                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                Add Comment
                            </button>
                        </div>
                    </div>
                    <div v-if="ticket.comments && ticket.comments.length > 0" class="space-y-4">
                        <div
                            v-for="comment in ticket.comments"
                            :key="comment.id"
                            class="border-l-4 border-blue-500 dark:border-blue-400 pl-4 py-2"
                        >
                            <div class="flex items-center justify-between mb-1">
                                <span class="font-medium text-sm text-gray-900 dark:text-white">{{ getUserName(comment.user_id) }}</span>
                                <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(comment.created_at) }}</span>
                            </div>
                            <p class="text-gray-700 dark:text-gray-300">{{ comment.comment }}</p>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 dark:text-gray-400 text-center py-8">No comments yet</p>
                </div>

                <!-- Attachments Tab -->
                <div v-show="activeTab === 'attachments'">
                    <div v-if="!['resolved', 'cancelled', 'closed'].includes(ticket.status)" class="mb-4">
                        <input
                            ref="fileInput"
                            type="file"
                            multiple
                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                            @change="uploadFiles"
                            class="hidden"
                        />
                        <button
                            @click="$refs.fileInput.click()"
                            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Upload Files
                        </button>
                    </div>
                    <div v-if="ticket.attachments && ticket.attachments.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div
                            v-for="attachment in ticket.attachments"
                            :key="attachment.id"
                            class="border dark:border-gray-700 rounded-lg p-4"
                        >
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <p class="font-medium text-sm text-gray-900 dark:text-white truncate">{{ attachment.file_name }}</p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ formatFileSize(attachment.file_size) }}</p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(attachment.created_at) }}</p>
                                </div>
                                <a
                                    :href="attachment.file_url"
                                    target="_blank"
                                    class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 dark:text-gray-400 text-center py-8">No attachments uploaded yet</p>
                </div>

                <!-- Logs Tab -->
                <div v-show="activeTab === 'logs'">
                    <div v-if="ticket.logs && ticket.logs.length > 0" class="space-y-4">
                        <div
                            v-for="log in ticket.logs"
                            :key="log.id"
                            class="flex items-start space-x-3 pb-4 border-b dark:border-gray-700 last:border-b-0"
                        >
                            <div class="flex-shrink-0 mt-1">
                                <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                    <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            <div class="flex-1">
                                <p class="text-sm">
                                    <span class="font-medium text-gray-900 dark:text-white">{{ getUserName(log.user_id) }}</span>
                                    <span class="text-gray-600 dark:text-gray-400"> {{ log.description }}</span>
                                </p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ formatDate(log.created_at) }}</p>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-gray-500 dark:text-gray-400 text-center py-8">No activity recorded yet</p>
                </div>

                <!-- SLA Policy Tab -->
                <div v-show="activeTab === 'sla'" v-if="ticket.sla_policy">
                    <div class="space-y-6">
                        <div class="flex items-center justify-between">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">SLA Information</h3>
                            <span
                                v-if="ticket.sla_status"
                                :class="[
                                    'px-3 py-1 text-xs font-semibold rounded-full',
                                    getSlaStatusClass(ticket.sla_status)
                                ]"
                            >
                                {{ getSlaStatusLabel(ticket.sla_status) }}
                            </span>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- SLA Policy -->
                            <div>
                                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">SLA Policy</dt>
                                <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                    {{ ticket.sla_policy.name }}
                                </dd>
                            </div>

                            <!-- Response SLA -->
                            <div>
                                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Response SLA</dt>
                                <dd class="mt-1 text-sm">
                                    <div v-if="ticket.sla_response_due_at" class="space-y-1">
                                        <div :class="ticket.sla_response_breached ? 'text-red-600 font-semibold' : ticket.sla_response_met_at ? 'text-green-600' : 'text-gray-900'">
                                            Due: {{ formatDateTime(ticket.sla_response_due_at) }}
                                        </div>
                                        <div v-if="ticket.sla_response_met_at" class="text-green-600 text-xs">
                                            Met at {{ formatDateTime(ticket.sla_response_met_at) }}
                                        </div>
                                        <div v-else-if="ticket.sla_response_breached" class="text-red-600 text-xs font-semibold">
                                            Breached
                                        </div>
                                        <div v-else class="text-gray-600 text-xs">
                                            {{ getTimeRemaining(ticket.sla_response_due_at) }}
                                        </div>
                                    </div>
                                    <span v-else class="text-gray-400">Not set</span>
                                </dd>
                            </div>

                            <!-- Resolution SLA -->
                            <div>
                                <dt class="text-sm font-medium text-gray-500">Resolution SLA</dt>
                                <dd class="mt-1 text-sm">
                                    <div v-if="ticket.sla_resolution_due_at" class="space-y-1">
                                        <div :class="ticket.sla_resolution_breached ? 'text-red-600 font-semibold' : ticket.sla_resolution_met_at ? 'text-green-600' : 'text-gray-900'">
                                            Due: {{ formatDateTime(ticket.sla_resolution_due_at) }}
                                        </div>
                                        <div v-if="ticket.sla_resolution_met_at" class="text-green-600 text-xs">
                                            Met at {{ formatDateTime(ticket.sla_resolution_met_at) }}
                                        </div>
                                        <div v-else-if="ticket.sla_resolution_breached" class="text-red-600 text-xs font-semibold">
                                            Breached
                                        </div>
                                        <div v-else class="text-gray-600 text-xs">
                                            {{ getTimeRemaining(ticket.sla_resolution_due_at) }}
                                        </div>
                                    </div>
                                    <span v-else class="text-gray-400">Not set</span>
                                </dd>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>

    <!-- Task Completion Modal -->
    <div v-if="showTaskCompletionModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Complete Task</h3>
            <p class="mb-4 text-gray-700 dark:text-gray-300">{{ taskToComplete?.task }}</p>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Completion Notes (optional)</label>
                <textarea
                    v-model="taskCompletionNotes"
                    rows="3"
                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                ></textarea>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="closeTaskCompletionModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Cancel
                </button>
                <button
                    @click="completeTask"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                    Mark Complete
                </button>
            </div>
        </div>
    </div>

    <!-- Task Edit Modal -->
    <div v-if="showTaskEditModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Task</h3>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Task Description *</label>
                <textarea
                    v-model="taskEditForm.task"
                    rows="3"
                    required
                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                ></textarea>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="closeTaskEditModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Cancel
                </button>
                <button
                    @click="saveTaskEdit"
                    :disabled="!taskEditForm.task.trim() || taskEditForm.processing"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    Save
                </button>
            </div>
        </div>
    </div>

    <!-- Labor Modal -->
    <div v-if="showLaborModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{{ laborForm.id ? 'Edit' : 'Add' }} Labor Entry</h3>
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                    <textarea
                        v-model="laborForm.description"
                        rows="2"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    ></textarea>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hours *</label>
                    <input
                        v-model="laborForm.hours"
                        type="number"
                        step="0.01"
                        min="0.01"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Cost *</label>
                    <input
                        v-model="laborForm.total_cost"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="closeLaborModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Cancel
                </button>
                <button
                    @click="saveLabor"
                    :disabled="laborForm.processing || !laborForm.description || !laborForm.hours || !laborForm.total_cost"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    {{ laborForm.id ? 'Update' : 'Add' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Material Modal -->
    <div v-if="showMaterialModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{{ materialForm.id ? 'Edit' : 'Add' }} Material</h3>
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                    <input
                        v-model="materialForm.name"
                        type="text"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                        v-model="materialForm.description"
                        rows="2"
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    ></textarea>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Units *</label>
                    <input
                        v-model="materialForm.units"
                        type="number"
                        step="0.01"
                        min="0.01"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Cost *</label>
                    <input
                        v-model="materialForm.total_cost"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="closeMaterialModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Cancel
                </button>
                <button
                    @click="saveMaterial"
                    :disabled="materialForm.processing || !materialForm.name || !materialForm.units || !materialForm.total_cost"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    {{ materialForm.id ? 'Update' : 'Add' }}
                </button>
            </div>
        </div>
    </div>

    <!-- External Repair Modal -->
    <div v-if="showExternalRepairModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{{ externalRepairForm.id ? 'Edit' : 'Add' }} External Repair</h3>
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Provider *</label>
                    <input
                        v-model="externalRepairForm.service_provider"
                        type="text"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                    <textarea
                        v-model="externalRepairForm.description"
                        rows="2"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    ></textarea>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cost *</label>
                    <input
                        v-model="externalRepairForm.cost"
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Invoice Number</label>
                    <input
                        v-model="externalRepairForm.invoice_number"
                        type="text"
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="closeExternalRepairModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Cancel
                </button>
                <button
                    @click="saveExternalRepair"
                    :disabled="externalRepairForm.processing || !externalRepairForm.service_provider || !externalRepairForm.description || !externalRepairForm.cost"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    {{ externalRepairForm.id ? 'Update' : 'Add' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Resolve Ticket Dialog -->
    <div v-if="showCompleteDialog" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resolve Ticket</h3>
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Resolution *</label>
                    <textarea
                        v-model="completeForm.resolution"
                        rows="3"
                        required
                        placeholder="Describe how the issue was resolved..."
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    ></textarea>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Signature (optional)</label>
                    <input
                        v-model="completeForm.signature"
                        type="text"
                        placeholder="Your name or initials"
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="showCompleteDialog = false"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Cancel
                </button>
                <button
                    @click="completeTicket"
                    :disabled="!completeForm.resolution || completeForm.processing"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                    Resolve
                </button>
            </div>
        </div>
    </div>

    <!-- Start Work Immediately Dialog -->
    <div v-if="showStartWorkDialog" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Start Work Immediately</h3>
            <p class="text-sm text-gray-600 mb-4">
                Start working on this ticket right now. The system will set the start time to now and you can specify when you expect to finish.
            </p>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expected End Time *</label>
                <input
                    v-model="startWorkForm.expected_end"
                    type="datetime-local"
                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="showStartWorkDialog = false"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Close
                </button>
                <button
                    @click="startWorkImmediately"
                    :disabled="!startWorkForm.expected_end || startWorkForm.processing"
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                    Start Work
                </button>
            </div>
        </div>
    </div>

    <!-- Cancel Ticket Dialog -->
    <div v-if="showCancelDialog" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cancel Ticket</h3>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for Cancellation</label>
                <textarea
                    v-model="cancelForm.reason"
                    rows="3"
                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                ></textarea>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="showCancelDialog = false"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Close
                </button>
                <button
                    @click="cancelTicket"
                    :disabled="cancelForm.processing"
                    class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                    Cancel Ticket
                </button>
            </div>
        </div>
    </div>

    <!-- Put on Hold Dialog -->
    <div v-if="showPutOnHoldDialog" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Put Ticket on Hold</h3>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
                <textarea
                    v-model="holdForm.reason"
                    rows="3"
                    class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                ></textarea>
            </div>
            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="showPutOnHoldDialog = false"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                    Close
                </button>
                <button
                    @click="putOnHold"
                    :disabled="holdForm.processing"
                    class="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                    Put on Hold
                </button>
            </div>
        </div>
    </div>

    <!-- Print PDF Dialog -->
    <div v-if="showPrintPdfDialog" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Print Ticket PDF</h3>
            <p class="text-sm text-gray-600 mb-4">Generate a PDF report for this ticket:</p>

            <div class="mb-4">
                <label class="flex items-center">
                    <input
                        type="checkbox"
                        v-model="printPdfForm.include_activity_log"
                        class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span class="ml-2 text-sm text-gray-700">Include Activity Log</span>
                </label>
                <p class="mt-1 ml-6 text-xs text-gray-500">Add a complete activity log to the PDF report</p>
            </div>

            <div class="mt-6 flex justify-end space-x-3">
                <button
                    @click="showPrintPdfDialog = false"
                    class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                    Cancel
                </button>
                <button
                    @click="printPdf"
                    :disabled="printPdfForm.processing"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    <svg v-if="printPdfForm.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Generate PDF
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';
import { useCurrency } from '@/Composables/useCurrency';
import { useTimezone } from '@/Composables/useTimezone';
import { useSecureAxios } from '@/Composables/useSecureAxios';
import route from '@/Utils/route';
import axios from 'axios';
import InfoButton from '@/Components/Common/InfoButton.vue';

// Get secure axios methods that include company context validation
const { post, put, patch, delete: del } = useSecureAxios();

// Access subscription features from Inertia shared props
const page = usePage();
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });

const props = defineProps({
    ticket: Object,
    reporter: Object,
    assignedTo: Object,
    taskStats: Object,
    costBreakdown: Object,
    users: Object,
    canAssign: Boolean,
    canEdit: Boolean,
    canModifyTasksOrCosts: Boolean,
    canComplete: Boolean,
    canCancel: Boolean,
    canReopen: Boolean,
    technicians: Array,
    isReporter: Boolean,
    isAssigned: Boolean,
});

const toast = useToast();

// Helper function to handle company context errors
const handleCompanyContextError = (error) => {
    // Check if this is a company context mismatch error
    if (error.response?.status === 409 && error.response?.data?.error === 'company_context_mismatch') {
        toast.error('Your company session has changed. The page has been refreshed to prevent data being saved to the wrong company. Please try again.');
        // Redirect to dashboard after a short delay to allow toast to show
        setTimeout(() => {
            router.visit('/dashboard');
        }, 1500);
        return true; // Error was handled
    }
    return false; // Error was not a context mismatch, caller should handle
};
const { formatMoney, currency, currencySymbol, company } = useCurrency();
const { formatDateTime: formatDateTimeInTZ, formatDate: formatDateInTZ } = useTimezone();
const activeTab = ref('tasks');
const newTask = ref('');
const newComment = ref('');

// Forms
const assignForm = ref({ assigned_to: props.ticket.assigned_to || '', processing: false });
const scheduleForm = ref({
    scheduled_start: '',
    scheduled_end: '',
    processing: false
});
const completeForm = ref({ resolution: '', signature: '', processing: false });
const cancelForm = ref({ reason: '', processing: false });
const holdForm = ref({ reason: '', processing: false });
const startWorkForm = ref({ expected_end: '', processing: false });

// Task completion modal
const showTaskCompletionModal = ref(false);
const taskToComplete = ref(null);
const taskCompletionNotes = ref('');

// Task edit modal
const showTaskEditModal = ref(false);
const taskEditForm = ref({ id: null, task: '', processing: false });

// Labor modal
const showLaborModal = ref(false);
const laborForm = ref({ id: null, description: '', hours: '', total_cost: '', processing: false });

// Material modal
const showMaterialModal = ref(false);
const materialForm = ref({ id: null, name: '', description: '', units: '', total_cost: '', processing: false });

// External Repair modal
const showExternalRepairModal = ref(false);
const externalRepairForm = ref({ id: null, service_provider: '', description: '', cost: '', invoice_number: '', processing: false });

// Dialogs
const showCompleteDialog = ref(false);
const showCancelDialog = ref(false);
const showPutOnHoldDialog = ref(false);
const showStartWorkDialog = ref(false);
const showPrintPdfDialog = ref(false);

// Print PDF Form
const printPdfForm = ref({ include_activity_log: false, processing: false });

// Can edit costs (only during in_progress or on_hold AND user has permission)
const canEditCosts = computed(() => {
    return ['in_progress', 'on_hold'].includes(props.ticket.status) && props.canModifyTasksOrCosts;
});

// Tabs configuration
const tabs = computed(() => {
    const baseTabs = [
        { key: 'tasks', label: 'Tasks', count: props.ticket.tasks?.length || 0 },
        { key: 'labor', label: 'Labor', count: props.ticket.labor?.length || 0 },
        { key: 'materials', label: 'Materials', count: props.ticket.materials?.length || 0 },
        { key: 'external', label: 'External Repairs', count: props.ticket.external_repairs?.length || 0 },
        { key: 'comments', label: 'Comments', count: props.ticket.comments?.length || 0 },
        { key: 'attachments', label: 'Attachments', count: props.ticket.attachments?.length || 0 },
        { key: 'logs', label: 'Logs', count: props.ticket.logs?.length || 0 },
    ];

    // Add SLA tab if ticket has SLA policy
    if (props.ticket.sla_policy) {
        baseTabs.push({ key: 'sla', label: 'SLA Policy' });
    }

    return baseTabs;
});

// Workflow Methods
const assignTicket = async () => {
    assignForm.value.processing = true;
    try {
        await post(route('tickets.assign', props.ticket.id), {
            assigned_to: assignForm.value.assigned_to
        });
        toast.success('Ticket assigned successfully');
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to assign ticket');
        }
    } finally {
        assignForm.value.processing = false;
    }
};

const scheduleTicket = async () => {
    scheduleForm.value.processing = true;
    try {
        await post(route('tickets.schedule', props.ticket.id), scheduleForm.value);
        toast.success('Ticket scheduled successfully');
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to schedule ticket');
        }
    } finally {
        scheduleForm.value.processing = false;
    }
};

const startTicket = async () => {
    try {
        await post(route('tickets.start', props.ticket.id));
        toast.success('Work started successfully');
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to start work');
        }
    }
};

const startWorkImmediately = async () => {
    startWorkForm.value.processing = true;
    try {
        await post(route('tickets.start', props.ticket.id), startWorkForm.value);
        toast.success('Work started successfully');
        showStartWorkDialog.value = false;
        startWorkForm.value.expected_end = '';
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to start work');
        }
    } finally {
        startWorkForm.value.processing = false;
    }
};

const putOnHold = async () => {
    holdForm.value.processing = true;
    try {
        await post(route('tickets.putOnHold', props.ticket.id), holdForm.value);
        toast.success('Ticket put on hold');
        showPutOnHoldDialog.value = false;
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to put on hold');
        }
    } finally {
        holdForm.value.processing = false;
    }
};

const resumeTicket = async () => {
    try {
        await post(route('tickets.resume', props.ticket.id));
        toast.success('Ticket resumed successfully');
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to resume ticket');
        }
    }
};

const completeTicket = async () => {
    completeForm.value.processing = true;
    try {
        await post(route('tickets.complete', props.ticket.id), completeForm.value);
        toast.success('Ticket resolved successfully');
        showCompleteDialog.value = false;
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to resolve ticket');
        }
    } finally {
        completeForm.value.processing = false;
    }
};

const cancelTicket = async () => {
    cancelForm.value.processing = true;
    try {
        await post(route('tickets.cancel', props.ticket.id), cancelForm.value);
        toast.success('Ticket cancelled');
        showCancelDialog.value = false;
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to cancel ticket');
        }
    } finally {
        cancelForm.value.processing = false;
    }
};

const reopenTicket = async () => {
    try {
        await post(route('tickets.reopen', props.ticket.id));
        toast.success('Ticket reopened');
        router.reload();
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to reopen ticket');
        }
    }
};

// Task Methods
const addTask = async () => {
    if (!newTask.value.trim()) return;
    try {
        await post(route('tickets.tasks.add', props.ticket.id), {
            task: newTask.value
        });
        newTask.value = '';
        toast.success('Task added successfully');
        router.reload({ only: ['ticket'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to add task');
        }
    }
};

const handleTaskToggle = (task) => {
    if (!task.is_completed) {
        // Task is being marked as complete - show modal for notes
        taskToComplete.value = task;
        taskCompletionNotes.value = '';
        showTaskCompletionModal.value = true;
    } else {
        // Task is being uncompleted - toggle directly
        toggleTask(task.id, null);
    }
};

const completeTask = async () => {
    if (!taskToComplete.value) return;
    await toggleTask(taskToComplete.value.id, taskCompletionNotes.value);
    closeTaskCompletionModal();
};

const closeTaskCompletionModal = () => {
    showTaskCompletionModal.value = false;
    taskToComplete.value = null;
    taskCompletionNotes.value = '';
};

const toggleTask = async (taskId, notes = null) => {
    try {
        const payload = notes !== null ? { notes } : {};
        await patch(route('tickets.toggle-task', props.ticket.id, taskId), payload);
        router.reload({ only: ['ticket'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to update task');
        }
    }
};

const openTaskCompletionModal = (task) => {
    taskToComplete.value = task;
    taskCompletionNotes.value = '';
    showTaskCompletionModal.value = true;
};

const openTaskEditModal = (task) => {
    taskEditForm.value = {
        id: task.id,
        task: task.task,
        processing: false
    };
    showTaskEditModal.value = true;
};

const closeTaskEditModal = () => {
    showTaskEditModal.value = false;
    taskEditForm.value = { id: null, task: '', processing: false };
};

const saveTaskEdit = async () => {
    if (!taskEditForm.value.task.trim()) return;

    taskEditForm.value.processing = true;
    try {
        await put(route('tickets.tasks.edit', props.ticket.id, taskEditForm.value.id), {
            task: taskEditForm.value.task
        });
        toast.success('Task updated successfully');
        closeTaskEditModal();
        router.reload({ only: ['ticket'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to update task');
        }
    } finally {
        taskEditForm.value.processing = false;
    }
};

const deleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
        await del(route('tickets.tasks.delete', props.ticket.id, taskId));
        toast.success('Task deleted successfully');
        router.reload({ only: ['ticket'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to delete task');
        }
    }
};

// Labor Methods
const openLaborModal = (labor = null) => {
    if (labor) {
        laborForm.value = {
            id: labor.id,
            description: labor.description,
            hours: labor.hours,
            total_cost: labor.total_cost,
            processing: false
        };
    } else {
        laborForm.value = { id: null, description: '', hours: '', total_cost: '', processing: false };
    }
    showLaborModal.value = true;
};

const closeLaborModal = () => {
    showLaborModal.value = false;
    laborForm.value = { id: null, description: '', hours: '', total_cost: '', processing: false };
};

const saveLabor = async () => {
    laborForm.value.processing = true;
    try {
        const url = laborForm.value.id
            ? route('tickets.labor.update', props.ticket.id, laborForm.value.id)
            : route('tickets.labor.add', props.ticket.id);
        const method = laborForm.value.id ? 'put' : 'post';

        await axios[method](url, {
            description: laborForm.value.description,
            hours: laborForm.value.hours,
            total_cost: laborForm.value.total_cost,
            _company_id: company.value?.id
        });

        toast.success(`Labor entry ${laborForm.value.id ? 'updated' : 'added'} successfully`);
        closeLaborModal();
        router.reload({ only: ['ticket', 'costBreakdown'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to save labor entry');
        }
    } finally {
        laborForm.value.processing = false;
    }
};

const deleteLabor = async (laborId) => {
    if (!confirm('Are you sure you want to delete this labor entry?')) return;

    try {
        await del(route('tickets.labor.delete', props.ticket.id, laborId));
        toast.success('Labor entry deleted successfully');
        router.reload({ only: ['ticket', 'costBreakdown'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to delete labor entry');
        }
    }
};

// Material Methods
const openMaterialModal = (material = null) => {
    if (material) {
        materialForm.value = {
            id: material.id,
            name: material.material_name,
            description: material.description || '',
            units: material.quantity,
            total_cost: material.total_cost,
            processing: false
        };
    } else {
        materialForm.value = { id: null, name: '', description: '', units: '', total_cost: '', processing: false };
    }
    showMaterialModal.value = true;
};

const closeMaterialModal = () => {
    showMaterialModal.value = false;
    materialForm.value = { id: null, name: '', description: '', units: '', total_cost: '', processing: false };
};

const saveMaterial = async () => {
    materialForm.value.processing = true;
    try {
        const url = materialForm.value.id
            ? route('tickets.materials.update', props.ticket.id, materialForm.value.id)
            : route('tickets.materials.add', props.ticket.id);
        const method = materialForm.value.id ? 'put' : 'post';

        await axios[method](url, {
            name: materialForm.value.name,
            description: materialForm.value.description,
            units: materialForm.value.units,
            total_cost: materialForm.value.total_cost,
            _company_id: company.value?.id
        });

        toast.success(`Material ${materialForm.value.id ? 'updated' : 'added'} successfully`);
        closeMaterialModal();
        router.reload({ only: ['ticket', 'costBreakdown'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to save material');
        }
    } finally {
        materialForm.value.processing = false;
    }
};

const deleteMaterial = async (materialId) => {
    if (!confirm('Are you sure you want to delete this material?')) return;

    try {
        await del(route('tickets.materials.delete', props.ticket.id, materialId));
        toast.success('Material deleted successfully');
        router.reload({ only: ['ticket', 'costBreakdown'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to delete material');
        }
    }
};

// External Repair Methods
const openExternalRepairModal = (repair = null) => {
    if (repair) {
        externalRepairForm.value = {
            id: repair.id,
            service_provider: repair.vendor_name,
            description: repair.description,
            cost: repair.cost,
            invoice_number: repair.invoice_number || '',
            processing: false
        };
    } else {
        externalRepairForm.value = { id: null, service_provider: '', description: '', cost: '', invoice_number: '', processing: false };
    }
    showExternalRepairModal.value = true;
};

const closeExternalRepairModal = () => {
    showExternalRepairModal.value = false;
    externalRepairForm.value = { id: null, service_provider: '', description: '', cost: '', invoice_number: '', processing: false };
};

const saveExternalRepair = async () => {
    externalRepairForm.value.processing = true;
    try {
        const url = externalRepairForm.value.id
            ? route('tickets.external-repairs.update', props.ticket.id, externalRepairForm.value.id)
            : route('tickets.external-repairs.add', props.ticket.id);
        const method = externalRepairForm.value.id ? 'put' : 'post';

        await axios[method](url, {
            service_provider: externalRepairForm.value.service_provider,
            description: externalRepairForm.value.description,
            cost: externalRepairForm.value.cost,
            invoice_number: externalRepairForm.value.invoice_number,
            _company_id: company.value?.id
        });

        toast.success(`External repair ${externalRepairForm.value.id ? 'updated' : 'added'} successfully`);
        closeExternalRepairModal();
        router.reload({ only: ['ticket', 'costBreakdown'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to save external repair');
        }
    } finally {
        externalRepairForm.value.processing = false;
    }
};

const deleteExternalRepair = async (repairId) => {
    if (!confirm('Are you sure you want to delete this external repair?')) return;

    try {
        await del(route('tickets.external-repairs.delete', props.ticket.id, repairId));
        toast.success('External repair deleted successfully');
        router.reload({ only: ['ticket', 'costBreakdown'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to delete external repair');
        }
    }
};

// File & Comment Methods
const uploadFiles = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    const formData = new FormData();
    Array.from(files).forEach(file => {
        formData.append('files[]', file);
    });

    try {
        await post(route('tickets.uploadFiles', props.ticket.id), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Files uploaded successfully');
        router.reload({ only: ['ticket'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to upload files');
        }
    }
};

const addComment = async () => {
    if (!newComment.value.trim()) return;
    try {
        await post(route('tickets.addComment', props.ticket.id), {
            comment: newComment.value
        });
        newComment.value = '';
        toast.success('Comment added successfully');
        router.reload({ only: ['ticket'] });
    } catch (error) {
        if (!handleCompanyContextError(error)) {
            toast.error(error.response?.data?.message || 'Failed to add comment');
        }
    }
};

// Helper methods
const getStatusClass = (status) => {
    const classes = {
        draft: 'bg-gray-100 text-gray-800',
        open: 'bg-blue-100 text-blue-800',
        assigned: 'bg-cyan-100 text-cyan-800',
        scheduled: 'bg-indigo-100 text-indigo-800',
        in_progress: 'bg-yellow-100 text-yellow-800',
        on_hold: 'bg-orange-100 text-orange-800',
        resolved: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800',
        re_opened: 'bg-purple-100 text-purple-800',
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'text-green-600',
        normal: 'text-blue-600',
        high: 'text-orange-600',
        urgent: 'text-red-600',
    };
    return classes[priority] || 'text-gray-600';
};

// Format date in company timezone
const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date); // Now timezone-aware using company timezone!
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const getUserName = (userId) => {
    const user = props.users?.[userId];
    return user?.name || 'Unknown User';
};

const getSlaStatusClass = (status) => {
    const classes = {
        on_track: 'bg-green-100 text-green-800',
        warning: 'bg-yellow-100 text-yellow-800',
        critical: 'bg-orange-100 text-orange-800',
        breached: 'bg-red-100 text-red-800',
        met: 'bg-blue-100 text-blue-800',
        breached_met: 'bg-purple-100 text-purple-800',
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
};

const getSlaStatusLabel = (status) => {
    const labels = {
        on_track: 'On Track',
        warning: 'Warning',
        critical: 'Critical',
        breached: 'Breached',
        met: 'Met',
        breached_met: 'Breached (Met)',
    };
    return labels[status] || status;
};

// Format date and time in company timezone
const formatDateTime = (datetime) => {
    if (!datetime) return 'N/A';
    return formatDateTimeInTZ(datetime); // Now timezone-aware using company timezone!
};

const getTimeRemaining = (dueAt) => {
    if (!dueAt) return '';
    const now = new Date();
    const due = new Date(dueAt);
    const diff = due - now;

    if (diff < 0) {
        const hours = Math.floor(Math.abs(diff) / (1000 * 60 * 60));
        return `Overdue by ${hours}h`;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
        return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
};

// Print PDF function
const printPdf = () => {
    printPdfForm.value.processing = true;

    // Create form data
    const url = route('tickets.pdf', props.ticket.id);
    const params = new URLSearchParams({
        include_activity_log: printPdfForm.value.include_activity_log ? '1' : '0',
    });

    // Open PDF in new window
    window.open(`${url}?${params.toString()}`, '_blank');

    // Reset and close dialog
    setTimeout(() => {
        printPdfForm.value.processing = false;
        printPdfForm.value.include_activity_log = false;
        showPrintPdfDialog.value = false;
    }, 1000);
};
</script>
